<template>
	<SortableTable
		v-model:search="search"
		:headers="headers"
		:items="glossaryStore.keywords"
		expand-all
		item-unique-key="path"
		show-pagination
		show-title
		title="Glossary terms"
		class="rounded-lg bg-white p-4 dark:bg-gray-800 dark:text-gray-200"
	>
		<template #toolbar>
			<!--
			     This will loop over existing hardcoded keywords & save them all to api endpoint
			     Get rid of it once everything's updated
			-->
			<BaseButton color="error" dark @click="addAllKeywords"> Add All keywords </BaseButton>

			<BaseButton color="primary" dark @click="addKeyword"> New Keyword </BaseButton>

			<BaseDialog v-model="showDialog" :loading="loading" max-width="50rem">
				<Form v-slot="{ meta }" class="w-[40rem]">
					<section class="space-y-5 rounded-lg bg-white p-4 dark:bg-gray-700">
						<h3 class="text-bold mb-4 text-center text-3xl">Edit Keyword</h3>
						<div class="flex h-[7rem] gap-2">
							<div class="basis-1/3 space-y-1">
								<Field
									v-slot="{ field, handleChange, handleBlur, errors }"
									v-model="keyword.title"
									name="Title"
									rules="required"
								>
									<TextFieldInput
										v-bind="field"
										label="Title"
										:error-messages="errors"
										@change="handleChange"
										@blur="handleBlur"
									>
										<template v-if="errors.length > 0" #message>
											Name is required
										</template>
									</TextFieldInput>
								</Field>

								<TextFieldInput v-model="keyword.path" label="Path" class="mb-4" />
							</div>

							<BaseTextArea
								v-model="keyword.description"
								class="h-full basis-2/3"
								:rows="4"
								label="Description"
							/>
						</div>

						<GlossaryContentTable
							v-model:items="keyword.examples"
							item-name="Examples"
						/>

						<GlossaryContentTable v-model:items="keyword.notes" item-name="Notes" />

						<footer class="mt-4 flex items-center justify-end gap-2">
							<BaseButton color="gray" text @click="cancelChanges">
								Cancel
							</BaseButton>
							<BaseButton
								color="orange"
								:disabled="!isFormModified || !meta.valid"
								@click="saveChanges"
							>
								Save
							</BaseButton>
						</footer>
					</section>
				</Form>
			</BaseDialog>
			<BaseDialog v-model="deleteDialog" max-width="600">
				<template #header>
					<h3>Are you sure you want to delete this term?</h3>
				</template>
				<div class="rounded-lg border-l-4 border-gray-500 bg-gray-100 p-4 dark:bg-gray-900">
					{{ keyword.title }}
				</div>
				<template #actions>
					<BaseButton color="blue" dense @click="cancelChanges">Cancel</BaseButton>
					<BaseButton color="error" dense @click="saveChanges">Delete</BaseButton>
				</template>
			</BaseDialog>
		</template>

		<template #item_expanded="keywordData">
			<h3 class="p-2 text-center text-3xl font-medium">Preview</h3>
			<div class="mt-0 p-2">
				<KeywordContent :keyword="keywordData.path" :preview="keywordData" />
			</div>
		</template>

		<template #item.title="{ title }">
			<span class="font-medium text-gray-900 dark:text-white sm:pl-6">
				{{ title }}
			</span>
		</template>
		<template #item.description="{ description }">
			<div>{{ description.substring(0, 60) }}...</div>
			<!-- <div class="whitespace-normal" v-html="description" /> -->
		</template>
		<template #item.examples="{ examples }">
			{{ examples?.length ?? 0 }}
		</template>
		<template #item.notes="{ notes }">
			{{ notes?.length ?? 0 }}
		</template>

		<template #item.actions="item">
			<div class="flex items-center justify-center gap-x-2">
				<InfoLink
					class="mr-1"
					icon-size="lg"
					:keyword="item.path"
					:show-text="false"
					tooltip-position="right"
				/>

				<button type="button" @click="updateKeyword({ item, mode: 'editing' })">
					<FAIcon class="mr-2 text-lg text-orange" icon="pencil" />
				</button>
				<button type="button" @click="updateKeyword({ item, mode: 'adding' })">
					<FAIcon class="mr-2 text-lg text-green-600" icon="copy" />
				</button>
				<button type="button" @click="updateKeyword({ item, mode: 'deleting' })">
					<FAIcon class="text-lg text-red" icon="trash" />
				</button>
			</div>
		</template>
	</SortableTable>
</template>

<script setup>
import { reactive, ref, onMounted, computed } from 'vue';
import { Form, Field } from 'vee-validate';

import InfoLink from '@/components/glossary/InfoLink';
import KeywordContent from '@/components/glossary/KeywordContent';
import keywordList from '@/components/glossary/keywordList';

import GlossaryContentTable from '@/components/MirusControls/GlossaryContentTable';

import { useGlossaryStore } from '@/stores/glossary';

import TextFieldInput from '@/components/ui/TextFieldInput';
import BaseButton from '@/components/ui/BaseButton';
import BaseTextArea from '@/components/ui/BaseTextArea';
import SortableTable from '@/components/ui/SortableTable';
import BaseDialog from '@/components/ui/BaseDialog';

const headers = [
	{ sortable: true, text: 'Title', value: 'title', filterable: true },
	{ sortable: false, text: 'Description', value: 'description', filterable: true },
	{ sortable: false, text: 'Examples', value: 'examples' },
	{ sortable: false, text: 'Notes', value: 'notes' },
	{ sortable: false, text: 'Actions', value: 'actions' },
];

const glossaryStore = useGlossaryStore();

const loading = ref(false);
const search = ref(``);
const showDialog = ref(false);
const deleteDialog = ref(false);
const initialFormData = ref('');

const mode = ref(null);
const id = ref(null);

const keyword = reactive({
	title: ``,
	description: ``,
	path: ``,
	visible: true,
	examples: [],
	notes: [],
});

const isFormModified = computed(() => {
	return JSON.stringify(keyword) !== initialFormData.value;
});

function resetKeyword() {
	keyword.title = ``;
	keyword.description = ``;
	keyword.path = ``;
	keyword.visible = true;
	keyword.examples = [];
	keyword.notes = [];
}

function updateKeyword({ item, mode: newMode }) {
	const { title, description, examples, notes, path } = item;
	keyword.title = title;
	keyword.description = description;
	keyword.path = path;
	keyword.examples = examples ? examples : [];
	keyword.notes = notes ? notes : [];

	initialFormData.value = JSON.stringify(keyword);

	mode.value = newMode;
	id.value = item.id;
	if (mode.value === 'deleting') {
		deleteDialog.value = true;
	} else {
		showDialog.value = true;
	}
}

function addKeyword() {
	resetKeyword();
	initialFormData.value = JSON.stringify(keyword);
	showDialog.value = true;
}
function cancelChanges() {
	mode.value = undefined;
	showDialog.value = false;
	deleteDialog.value = false;
	resetKeyword();
}
async function saveChanges() {
	loading.value = true;
	let action;
	switch (mode.value) {
		case 'editing':
			action = `updateKeyword`;
			break;
		case 'deleting':
			action = `deleteKeyword`;
			break;
		default:
			action = `saveKeyword`;
	}
	await glossaryStore[action]({
		json: { ...keyword },
		id: id.value,
	});

	loading.value = false;
	showDialog.value = false;
	deleteDialog.value = false;
	mode.value = null;
	id.value = undefined;
	resetKeyword();
}
async function addAllKeywords() {
	Object.entries(keywordList).forEach(async ([key, value]) => {
		updateKeyword({
			item: {
				...value,
				path: key,
				examples: value.examples || [],
				notes: value.notes || [],
			},
		});
		await saveChanges();
	});
}
onMounted(async () => {
	loading.value = true;
	await glossaryStore.getKeywordList();
	loading.value = false;
});
</script>
